/**
 * Modelo FormaPago - Formas de Pago
 * Separado en sub-interfaces según el tipo de pago,
 * ya que no todos los tipos requieren los mismos campos.
 */

// Enum con los tipos de pago disponibles
export enum PaymentMethodType {
  Efectivo = 0,
  Transferencia = 1,
  TarjetaDebito = 2,
  TarjetaCredito = 3,
  Cheque = 4,
  BilleteraVirtual = 5,
  Pix = 6,
  Otro = 7,
}

// Labels para mostrar en UI
export const PaymentMethodTypeLabels: Record<PaymentMethodType, string> = {
  [PaymentMethodType.Efectivo]: 'Efectivo',
  [PaymentMethodType.Transferencia]: 'Transferencia',
  [PaymentMethodType.TarjetaDebito]: 'Tarjeta Débito',
  [PaymentMethodType.TarjetaCredito]: 'Tarjeta Crédito',
  [PaymentMethodType.Cheque]: 'Cheque',
  [PaymentMethodType.BilleteraVirtual]: 'Billetera Virtual',
  [PaymentMethodType.Pix]: 'Pix',
  [PaymentMethodType.Otro]: 'Otro',
};

// Campos base comunes a todos los tipos de pago
export interface PaymentMethodBase {
  id: number;
  descripcion?: string;
  tipo: PaymentMethodType;
  empresa: number; // FK a Empresa
  activo: boolean;
}

// Efectivo: solo campos base, sin datos bancarios
export interface PaymentMethodEfectivo extends PaymentMethodBase {
  tipo: PaymentMethodType.Efectivo;
}

// Transferencia: datos bancarios completos + CBU/alias
export interface PaymentMethodTransferencia extends PaymentMethodBase {
  tipo: PaymentMethodType.Transferencia;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  cbu?: string;
  alias_cbu?: string;
  cuit_cuil?: string;
}

// Tarjeta Débito: datos bancarios básicos
export interface PaymentMethodTarjetaDebito extends PaymentMethodBase {
  tipo: PaymentMethodType.TarjetaDebito;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
}

// Tarjeta Crédito: datos bancarios básicos
export interface PaymentMethodTarjetaCredito extends PaymentMethodBase {
  tipo: PaymentMethodType.TarjetaCredito;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
}

// Cheque: datos bancarios + CUIT
export interface PaymentMethodCheque extends PaymentMethodBase {
  tipo: PaymentMethodType.Cheque;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  cuit_cuil?: string;
}

// Billetera Virtual: clave/alias + email de pago
export interface PaymentMethodBilleteraVirtual extends PaymentMethodBase {
  tipo: PaymentMethodType.BilleteraVirtual;
  nombre_cuenta?: string;
  clave?: string;
  email_pago?: string;
}

// Pix (Brasil): clave + email de pago
export interface PaymentMethodPix extends PaymentMethodBase {
  tipo: PaymentMethodType.Pix;
  clave?: string;
  email_pago?: string;
}

// Otro: todos los campos opcionales disponibles
export interface PaymentMethodOtro extends PaymentMethodBase {
  tipo: PaymentMethodType.Otro;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  clave?: string;
  cbu?: string;
  alias_cbu?: string;
  cuit_cuil?: string;
  email_pago?: string;
}

// Union discriminada por 'tipo' que agrupa todos los sub-modelos
export type PaymentMethod =
  | PaymentMethodEfectivo
  | PaymentMethodTransferencia
  | PaymentMethodTarjetaDebito
  | PaymentMethodTarjetaCredito
  | PaymentMethodCheque
  | PaymentMethodBilleteraVirtual
  | PaymentMethodPix
  | PaymentMethodOtro;

/**
 * Campos relevantes por tipo de pago.
 * Útil para renderizar formularios dinámicos según el tipo seleccionado.
 */
export const PaymentMethodFieldsByType: Record<PaymentMethodType, string[]> = {
  [PaymentMethodType.Efectivo]: [],
  [PaymentMethodType.Transferencia]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'cbu',
    'alias_cbu',
    'cuit_cuil',
  ],
  [PaymentMethodType.TarjetaDebito]: ['banco', 'nombre_cuenta', 'numero_cuenta'],
  [PaymentMethodType.TarjetaCredito]: ['banco', 'nombre_cuenta', 'numero_cuenta'],
  [PaymentMethodType.Cheque]: ['banco', 'nombre_cuenta', 'numero_cuenta', 'cuit_cuil'],
  [PaymentMethodType.BilleteraVirtual]: ['nombre_cuenta', 'clave', 'email_pago'],
  [PaymentMethodType.Pix]: ['clave', 'email_pago'],
  [PaymentMethodType.Otro]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'clave',
    'cbu',
    'alias_cbu',
    'cuit_cuil',
    'email_pago',
  ],
};
